import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const maxAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    let isMounted = true;

    const pollPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        if (isMounted) {
          setStatus('timeout');
        }
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        const data = response.data;
        
        if (data.payment_status === 'paid') {
          if (isMounted) {
            setPaymentData(data);
            setStatus('success');
          }
          return;
        } else if (data.status === 'expired') {
          if (isMounted) {
            setStatus('expired');
          }
          return;
        }
        
        // Continue polling if still pending
        attempts++;
        setTimeout(pollPaymentStatus, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (isMounted) {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-brand-surface" data-testid="payment-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand-navy mx-auto mb-4" />
          <h2 className="font-heading font-semibold text-xl text-brand-navy mb-2">
            Verifying Payment...
          </h2>
          <p className="font-body text-brand-charcoal">
            Please wait while we confirm your payment.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success' && paymentData) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-brand-surface p-5" data-testid="payment-success">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-brand-navy mb-3">
            Payment Successful!
          </h1>
          <p className="font-body text-brand-charcoal mb-6">
            Thank you for your payment. Your cleaning service has been confirmed.
          </p>
          
          <div className="bg-brand-surface rounded-xl p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-5 h-5 text-brand-navy" />
              <span className="font-heading font-semibold text-brand-navy">Payment Details</span>
            </div>
            <div className="space-y-2 font-body text-sm">
              <div className="flex justify-between">
                <span className="text-brand-charcoal">Service:</span>
                <span className="text-brand-navy font-medium">{paymentData.package_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-charcoal">Amount:</span>
                <span className="text-brand-navy font-medium">
                  ${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-charcoal">Status:</span>
                <span className="text-green-600 font-medium capitalize">{paymentData.payment_status}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body text-sm rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-brand-surface p-5" data-testid="payment-error">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-brand-navy mb-3">
          {status === 'timeout' ? 'Verification Timeout' : 
           status === 'expired' ? 'Session Expired' : 'Payment Error'}
        </h1>
        <p className="font-body text-brand-charcoal mb-6">
          {status === 'timeout' 
            ? 'We were unable to verify your payment status. Please check your email for confirmation or contact support.'
            : status === 'expired'
            ? 'Your payment session has expired. Please try again.'
            : 'There was an issue processing your payment. Please try again or contact support.'}
        </p>
        
        <div className="space-y-3">
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full"
          >
            Go to Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="w-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body text-sm rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
